import { useEffect, useRef, useState } from 'react';
import type { Edit, PdfPage } from '@pdf-ticket/pdf-core';
import type { ShapeAdjustHandle } from '@pdf-ticket/editor';
import { loadViewerDocument, renderPageToCanvas, type PDFDocumentProxy } from '../lib/pdf-render.js';
import { useEditorStore, type EditorTool, type PendingImage } from '../lib/editor-store.js';
import { EMBED_FALLBACK_FONT_REF } from '../lib/cjk-font.js';
import { effectiveRotations, rotateCW, type Rotation } from '../lib/effective-rotation.js';
import { effectiveOrder } from '../lib/effective-order.js';
import {
  pdfRectToCssRectRotated,
  cssDeltaToPdfDelta,
  cssRectToPdfRectRotated,
  clickToPdfPointRotated,
  type RotationDelta,
} from '../lib/page-coords.js';
import { getPageTexts } from '../lib/page-text-cache.js';
import { runsInRect, markupEditsForRuns, type MarkupKind } from '../lib/text-markup.js';
import {
  decimate,
  freehandInkStrokes,
  inkStrokeSvgPoints,
  freehandSvgD,
  FREEHAND_SHAPE_TYPE,
  type Pt,
} from '../lib/freehand.js';
import {
  shapePathInPdf,
  shapeSvgPathD,
  shapeAdjustSpec,
  DEFAULT_SHAPE_FILL,
  DEFAULT_SHAPE_STROKE,
  DEFAULT_SHAPE_STROKE_WIDTH,
  isMarkShape,
  markDefaultStyle,
} from '../lib/shape-svg.js';
import {
  fitPageScale,
  fitWidthScale,
  nextZoomIn,
  nextZoomOut,
  zoomLabel,
  type ZoomMode,
} from '../lib/zoom.js';

/**
 * Viewer — renders the loaded PDF's pages as pdf.js canvases. Display only; the
 * driver (client-side) owns the byte-preserving edit/save. Page metadata + the
 * rotate action come from the editor store. Pending rotate edits are reflected
 * live by re-rendering each canvas at its effective rotation; pending placement
 * edits (annotation / highlight / text) are previewed via DOM overlays since
 * pdf.js renders from the original bytes and can't reflect them otherwise.
 */

// Default sizes for click-placed edits. Highlights are wide+short like a marker
// stroke; sticky notes are square; text uses a typical body size.
const NOTE_SIZE_PT = 24;
const HIGHLIGHT_W = 120;
const HIGHLIGHT_H = 16;
const TEXT_SIZE_PT = 12;
// Default shape size (PDF points) for a bare click (no drag) with a shape armed.
const SHAPE_DEFAULT_W = 96;
const SHAPE_DEFAULT_H = 72;

type Rgb = { readonly r: number; readonly g: number; readonly b: number };

/** Current shape style for NEW shapes (fill/outline color, width, opacity). */
type ShapeStyle = {
  readonly fill: Rgb | null;
  readonly stroke: Rgb | null;
  readonly strokeWidth: number;
  readonly opacity: number;
};

// Annotation color palette (each channel 0–1, matching the driver/api color
// contract). The first entry is the default highlight yellow.
const COLOR_PRESETS: ReadonlyArray<{ name: string; rgb: Rgb }> = [
  { name: 'イエロー', rgb: { r: 1, g: 0.9, b: 0.2 } },
  { name: 'グリーン', rgb: { r: 0.45, g: 0.85, b: 0.4 } },
  { name: 'ピンク', rgb: { r: 0.96, g: 0.45, b: 0.66 } },
  { name: 'ブルー', rgb: { r: 0.4, g: 0.68, b: 1 } },
  { name: 'オレンジ', rgb: { r: 1, g: 0.58, b: 0.2 } },
];

const rgbCss = (c: Rgb, alpha = 1): string =>
  `rgba(${Math.round(c.r * 255)}, ${Math.round(c.g * 255)}, ${Math.round(c.b * 255)}, ${alpha})`;

const sameRgb = (a: Rgb, b: Rgb): boolean => a.r === b.r && a.g === b.g && a.b === b.b;

// <input type="color"> uses "#rrggbb"; the edit/driver contract uses 0–1 RGB.
const rgbToHex = (c: Rgb): string => {
  const h = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
};
const hexToRgb = (hex: string): Rgb => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[1]!, 16) / 255, g: parseInt(m[2]!, 16) / 255, b: parseInt(m[3]!, 16) / 255 };
};
const isPreset = (c: Rgb): boolean => COLOR_PRESETS.some((p) => sameRgb(p.rgb, c));

/**
 * A labeled color picker row (presets + custom + "none") used by the shape
 * style bar for fill and outline. `null` means no paint (the ⌀ swatch).
 */
function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Rgb | null;
  onChange: (c: Rgb | null) => void;
}) {
  return (
    <span className="pv__palette pv__style-row" role="group" aria-label={label}>
      <span className="pv__style-label">{label}</span>
      {COLOR_PRESETS.map((p) => (
        <button
          key={p.name}
          type="button"
          className={`swatch ${value && sameRgb(p.rgb, value) ? 'swatch--on' : ''}`.trim()}
          style={{ background: rgbCss(p.rgb) }}
          title={p.name}
          aria-label={p.name}
          aria-pressed={!!value && sameRgb(p.rgb, value)}
          onClick={() => onChange(p.rgb)}
        />
      ))}
      <label
        className={`swatch swatch--custom ${value && !isPreset(value) ? 'swatch--on' : ''}`.trim()}
        title="カスタムカラー"
        style={value && !isPreset(value) ? { background: rgbCss(value) } : undefined}
      >
        <input
          type="color"
          value={rgbToHex(value ?? { r: 0, g: 0, b: 0 })}
          onChange={(e) => onChange(hexToRgb(e.target.value))}
          aria-label={`${label}: カスタムカラー`}
        />
      </label>
      <button
        type="button"
        className={`swatch swatch--none ${value === null ? 'swatch--on' : ''}`.trim()}
        title="なし"
        aria-label={`${label}: なし`}
        aria-pressed={value === null}
        onClick={() => onChange(null)}
      />
    </span>
  );
}

function PageCanvas({
  doc,
  pageNumber,
  rotation,
  scale,
  onCanvas,
}: {
  doc: PDFDocumentProxy;
  pageNumber: number;
  rotation: number;
  /** Zoom multiplier (1 = 100%). Re-renders the raster when it changes. */
  scale: number;
  /** Receives the mounted <canvas> element. Lets PageView lift it into state so the overlay re-renders. */
  onCanvas: (el: HTMLCanvasElement | null) => void;
}) {
  // Use a *state* (not a plain ref) for the mounted node so the parent's
  // overlay knows when the canvas exists. The render effect still keys on it.
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    onCanvas(canvas);
  }, [canvas, onCanvas]);
  useEffect(() => {
    if (canvas) void renderPageToCanvas(doc, pageNumber, canvas, scale, rotation).catch(() => undefined);
  }, [canvas, doc, pageNumber, rotation, scale]);
  return <canvas ref={setCanvas} className="page-canvas" />;
}

/**
 * Pending-edit overlay layer for one page. Positions each add-annotation /
 * add-highlight / add-text edit as a CSS-absolute box on top of the canvas.
 */
function PageOverlay({
  edits,
  pageIndex,
  page,
  rotation,
  canvasEl,
  interactive,
  removeEdit,
}: {
  edits: ReadonlyArray<Edit>;
  pageIndex: number;
  page: PdfPage;
  /** Effective (post-edit) rotation the canvas is currently rendered at. */
  rotation: number;
  canvasEl: HTMLCanvasElement | null;
  /** Select mode: overlays accept clicks to remove their underlying edit. */
  interactive: boolean;
  removeEdit: (index: number) => void;
}) {
  // Re-measure when the canvas resizes (rotation re-render swaps dims).
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!canvasEl) return;
    const update = () => setBox({ width: canvasEl.clientWidth, height: canvasEl.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(canvasEl);
    return () => ro.disconnect();
  }, [canvasEl]);

  const searchHighlight = useEditorStore((s) => s.searchHighlight);
  const updateEditPosition = useEditorStore((s) => s.updateEditPosition);
  // Live drag offset (visual) for the overlay being moved; ref holds the drag
  // origin (start client coords + the edit's PDF anchor at grab time).
  const [liveDrag, setLiveDrag] = useState<{ index: number; dx: number; dy: number } | null>(null);
  const dragRef = useRef<{ index: number; startX: number; startY: number; ex: number; ey: number } | null>(null);
  const updateEditText = useEditorStore((s) => s.updateEditText);
  const updateEditSize = useEditorStore((s) => s.updateEditSize);
  const updateEditBounds = useEditorStore((s) => s.updateEditBounds);
  const updateEditRotation = useEditorStore((s) => s.updateEditRotation);
  // Inline content edit (✎ badge) for a placed text/note overlay.
  const [editing, setEditing] = useState<{
    index: number;
    value: string;
    css: { left: number; top: number; width: number; fontPx: number };
  } | null>(null);
  // Live resize (↘ handle): visual w/h delta in CSS px; ref holds the start.
  const [liveResize, setLiveResize] = useState<{ index: number; dw: number; dh: number } | null>(null);
  const resizeRef = useRef<{ index: number; startX: number; startY: number; left0: number; top0: number; w0: number; h0: number } | null>(null);
  // Live rotation (⟳ handle): visual degrees; ref holds the shape center + start.
  const [liveRotate, setLiveRotate] = useState<{ index: number; rotation: number } | null>(null);
  const rotateRef = useRef<{ index: number; cx: number; cy: number; startAngle: number; startRot: number } | null>(null);
  const updateEditAdjustments = useEditorStore((s) => s.updateEditAdjustments);
  const selectEdit = useEditorStore((s) => s.selectEdit);
  const toggleEditSelection = useEditorStore((s) => s.toggleEditSelection);
  const selectedEditIndices = useEditorStore((s) => s.selectedEditIndices);
  const translateEdits = useEditorStore((s) => s.translateEdits);
  const scaleEdits = useEditorStore((s) => s.scaleEdits);
  const removeEdits = useEditorStore((s) => s.removeEdits);
  // length===1 → single-select affordances (per-item handles, style target).
  // length>=2 → group bounding box + bulk handles; per-item handles suppressed.
  const multi = selectedEditIndices.length >= 2;
  const moveLineBy = useEditorStore((s) => s.moveLineBy);
  const updateLineEndpoint = useEditorStore((s) => s.updateLineEndpoint);
  // Live line drag (move / endpoint reshape): CSS-space endpoints; ref holds the start.
  const [liveLine, setLiveLine] = useState<{ index: number; p1: { x: number; y: number }; p2: { x: number; y: number } } | null>(null);
  const lineRef = useRef<{
    index: number;
    mode: 'move' | 'start' | 'end';
    startX: number;
    startY: number;
    p1: { x: number; y: number };
    p2: { x: number; y: number };
  } | null>(null);
  // Live adjustment (◆ handles): visual adj values; ref holds the shape box rect.
  const [liveAdjust, setLiveAdjust] = useState<{ index: number; adjustments: number[] } | null>(null);
  const adjustRef = useRef<{ index: number; left: number; top: number; width: number; height: number } | null>(null);
  // Group bbox live transform (bulk move = ghost translate, scale = ghost resize).
  const [groupLive, setGroupLive] = useState<{ mode: 'move' | 'scale'; dx: number; dy: number } | null>(null);
  const groupRef = useRef<{
    mode: 'move' | 'scale';
    startX: number;
    startY: number;
    css: { left: number; top: number; width: number; height: number };
    pdf: { x: number; y: number; width: number; height: number };
  } | null>(null);

  if (!box || box.width === 0) return null;

  // Additional clockwise rotation applied beyond the page's original display
  // orientation (overlays were placed in that original frame). 0 in the common
  // case; non-zero only after the user queues a rotate on an annotated page.
  const delta = (((rotation - page.rotation) % 360) + 360) % 360 as RotationDelta;
  const pageDims = { widthPt: page.width, heightPt: page.height };
  const toCss = (r: { x: number; y: number; width: number; height: number }) =>
    pdfRectToCssRectRotated(r, pageDims, box, delta);

  // PDF point ↔ CSS point, both rotation-aware (via the page-coords transforms),
  // so line endpoints render and reshape correctly at any page rotation.
  const sxScale = box.width / pageDims.widthPt;
  const syScale = box.height / pageDims.heightPt;
  const toCssPt = (px: number, py: number): { x: number; y: number } => {
    const r = toCss({ x: px, y: py, width: 0, height: 0 });
    return { x: r.left, y: r.top };
  };
  const cssPtToPdf = (cx: number, cy: number): { x: number; y: number } =>
    clickToPdfPointRotated(cx, cy, { left: 0, top: 0 }, { ...pageDims, rect: box }, delta);

  function endGroupDrag(ev: React.PointerEvent): void {
    const d = groupRef.current;
    groupRef.current = null;
    setGroupLive(null);
    if (!d) return;
    const dx = ev.clientX - d.startX;
    const dy = ev.clientY - d.startY;
    if (d.mode === 'move') {
      if (Math.abs(dx) + Math.abs(dy) > MOVE_PX && box) {
        // CSS delta → PDF delta, rotation-aware (group move works on rotated pages).
        const pd = cssDeltaToPdfDelta(dx, dy, pageDims, box, delta);
        translateEdits(selectedEditIndices, pd.dx, pd.dy);
      }
      return;
    }
    // Scale about the group's display top-left = PDF (minX, maxY). The scale
    // handle is only shown at delta === 0 (the store's scale anchor model assumes
    // screen-up = PDF-up), so this branch only runs on unrotated pages.
    const sx = Math.max(0.05, (d.css.width + dx) / d.css.width);
    const sy = Math.max(0.05, (d.css.height + dy) / d.css.height);
    if (sx !== 1 || sy !== 1) {
      scaleEdits(selectedEditIndices, d.pdf.x, d.pdf.y + d.pdf.height, sx, sy);
    }
  }

  function groupHandleProps(
    mode: 'move' | 'scale',
    cssBox: { left: number; top: number; width: number; height: number },
    pdfBox: { x: number; y: number; width: number; height: number },
  ): Record<string, unknown> {
    return {
      onPointerDown: (ev: React.PointerEvent) => {
        ev.stopPropagation();
        try {
          (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
        } catch {
          /* best-effort */
        }
        groupRef.current = { mode, startX: ev.clientX, startY: ev.clientY, css: cssBox, pdf: pdfBox };
        setGroupLive({ mode, dx: 0, dy: 0 });
      },
      onPointerMove: (ev: React.PointerEvent) => {
        const d = groupRef.current;
        if (!d) return;
        setGroupLive({ mode: d.mode, dx: ev.clientX - d.startX, dy: ev.clientY - d.startY });
      },
      onPointerUp: endGroupDrag,
    };
  }

  // In select mode an overlay can be DRAGGED to reposition its edit, or clicked
  // (no movement) to delete it. `i` is the edit's GLOBAL index in the store
  // (flatMap preserves it). Dragging is gated to delta === 0 (no extra rotation),
  // matching placement; rotated overlays stay click-to-delete only. In placement
  // mode overlays are inert (pointer-events:none via CSS) so clicks place edits.
  const MOVE_PX = 4; // below this, a pointer gesture counts as a click (delete)

  function endPointer(ev: React.PointerEvent): void {
    const d = dragRef.current;
    dragRef.current = null;
    setLiveDrag(null);
    if (!d || !box) return;
    const dx = ev.clientX - d.startX;
    const dy = ev.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > MOVE_PX) {
      // Moved: CSS delta → PDF delta, rotation-aware so a drag on a rotated
      // page moves the edit in the matching PDF direction (page-coords tests
      // round-trip all four rotations). The live preview is a screen-space CSS
      // translate, so it already follows the cursor at any rotation.
      const pd = cssDeltaToPdfDelta(dx, dy, pageDims, box, delta);
      updateEditPosition(d.index, { x: d.ex + pd.dx, y: d.ey + pd.dy });
    } else {
      // A click with no movement SELECTS the edit; Shift/Cmd toggles it in/out
      // of the multi-selection. Deletion is still only via the ✕ button.
      if (ev.shiftKey || ev.metaKey || ev.ctrlKey) toggleEditSelection(d.index);
      else selectEdit(d.index);
    }
  }

  function itemProps(i: number, label: string, ex: number, ey: number): Record<string, unknown> {
    if (!interactive) return { title: label };
    // The body is a DRAG handle, not a delete target — a click/touch must NOT
    // delete (deletion is only via the ✕ button). Move works at every page
    // rotation now: the commit maps the CSS drag through cssDeltaToPdfDelta.
    return {
      title: `ドラッグで移動（削除は ✕）: ${label}`,
      onPointerDown: (ev: React.PointerEvent) => {
        ev.stopPropagation();
        try {
          (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
        } catch {
          /* pointer capture is a best-effort enhancement; ignore if unavailable */
        }
        dragRef.current = { index: i, startX: ev.clientX, startY: ev.clientY, ex, ey };
        setLiveDrag({ index: i, dx: 0, dy: 0 });
      },
      onPointerMove: (ev: React.PointerEvent) => {
        const d = dragRef.current;
        if (!d) return;
        setLiveDrag({ index: d.index, dx: ev.clientX - d.startX, dy: ev.clientY - d.startY });
      },
      onPointerUp: endPointer,
    };
  }

  // Live move offset (CSS px) for an overlay being dragged — either as the lone
  // dragged item, OR as a member of a group being moved (so every selected
  // overlay follows the cursor, not just the group's dashed box).
  const liveMoveDelta = (i: number): { dx: number; dy: number } | null => {
    if (liveDrag && liveDrag.index === i) return { dx: liveDrag.dx, dy: liveDrag.dy };
    if (groupLive && groupLive.mode === 'move' && selectedEditIndices.includes(i))
      return { dx: groupLive.dx, dy: groupLive.dy };
    return null;
  };

  // Visual offset applied to the overlay currently being dragged (or group-moved).
  const dragStyle = (i: number): React.CSSProperties => {
    const d = liveMoveDelta(i);
    return d ? { transform: `translate(${d.dx}px, ${d.dy}px)`, zIndex: 20, cursor: 'grabbing' } : {};
  };

  // Live size override applied to the overlay being resized (visual feedback).
  const resizeStyle = (i: number, r: { width: number; height: number }): React.CSSProperties =>
    liveResize && liveResize.index === i
      ? { width: Math.max(8, r.width + liveResize.dw), height: Math.max(8, r.height + liveResize.dh), zIndex: 20 }
      : {};

  function endResize(ev: React.PointerEvent): void {
    const d = resizeRef.current;
    resizeRef.current = null;
    setLiveResize(null);
    if (!d || !box) return;
    const dw = ev.clientX - d.startX;
    const dh = ev.clientY - d.startY;
    // Grow the on-screen box at the ↘ corner (top-left screen-anchored, matching
    // the live preview), then invert the whole rotated CSS rect to a PDF rect so
    // the commit lands exactly where the box was drawn — at any page rotation.
    const screen = {
      left: d.left0,
      top: d.top0,
      width: Math.max(8, d.w0 + dw),
      height: Math.max(8, d.h0 + dh),
    };
    updateEditBounds(d.index, cssRectToPdfRectRotated(screen, pageDims, box, delta));
  }

  // ↘ resize handle (bottom-right). Works at every page rotation now (the commit
  // inverts the rotated screen rect). `r` is the overlay's current CSS rect.
  function resizeHandle(
    i: number,
    r: { left: number; top: number; width: number; height: number },
  ): React.ReactElement | null {
    if (!interactive || multi) return null;
    return (
      <span
        className="ovl__resize"
        title="ドラッグでリサイズ"
        onPointerDown={(ev) => {
          ev.stopPropagation();
          try {
            (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
          } catch {
            /* best-effort */
          }
          resizeRef.current = {
            index: i,
            startX: ev.clientX,
            startY: ev.clientY,
            left0: r.left,
            top0: r.top,
            w0: r.width,
            h0: r.height,
          };
          setLiveResize({ index: i, dw: 0, dh: 0 });
        }}
        onPointerMove={(ev) => {
          const d = resizeRef.current;
          if (!d) return;
          setLiveResize({ index: d.index, dw: ev.clientX - d.startX, dh: ev.clientY - d.startY });
        }}
        onPointerUp={endResize}
      />
    );
  }

  function endRotate(): void {
    const d = rotateRef.current;
    rotateRef.current = null;
    const lr = liveRotate;
    setLiveRotate(null);
    if (!d) return;
    if (lr && lr.index === d.index) updateEditRotation(d.index, lr.rotation);
  }

  // ⟳ rotation handle (above the shape). Drag to rotate about the shape center;
  // Shift snaps to 15°. Only for shapes at the unrotated page frame (delta===0).
  function rotateHandle(i: number, rot: number): React.ReactElement | null {
    if (!interactive || delta !== 0 || multi) return null;
    return (
      <span
        className="ovl__rotate"
        title="ドラッグで回転（Shift で 15°）"
        onPointerDown={(ev) => {
          ev.stopPropagation();
          const shapeEl = (ev.currentTarget as HTMLElement).closest('.ovl--shape') as HTMLElement | null;
          const rc = shapeEl?.getBoundingClientRect();
          if (!rc) return;
          const cx = rc.left + rc.width / 2;
          const cy = rc.top + rc.height / 2;
          const startAngle = (Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180) / Math.PI;
          rotateRef.current = { index: i, cx, cy, startAngle, startRot: rot };
          try {
            (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
          } catch {
            /* best-effort */
          }
          setLiveRotate({ index: i, rotation: rot });
        }}
        onPointerMove={(ev) => {
          const d = rotateRef.current;
          if (!d) return;
          const ang = (Math.atan2(ev.clientY - d.cy, ev.clientX - d.cx) * 180) / Math.PI;
          let next = d.startRot + (ang - d.startAngle);
          if (ev.shiftKey) next = Math.round(next / 15) * 15;
          next = ((next % 360) + 360) % 360;
          setLiveRotate({ index: d.index, rotation: next });
        }}
        onPointerUp={endRotate}
      />
    );
  }

  // ◆ adjustment handle (PowerPoint yellow handle). Positioned by the shape's
  // adjustment spec; dragging maps the pointer (normalized within the box) back
  // to new adj values and regenerates the path live, committing on release.
  // Gated to the unrotated frame for now (delta===0 and shape rotation 0).
  function adjustHandle(
    i: number,
    hi: number,
    handle: ShapeAdjustHandle,
    adj: readonly number[],
    bounds: { x: number; y: number; width: number; height: number },
  ): React.ReactElement | null {
    if (!interactive || delta !== 0 || multi) return null;
    const [nx, ny] = handle.position(adj, bounds);
    return (
      <span
        key={`adj-${hi}`}
        className="ovl__adjust"
        title="調整ハンドル（ドラッグで図形を調整）"
        style={{ left: `${nx * 100}%`, top: `${(1 - ny) * 100}%` }}
        onPointerDown={(ev) => {
          ev.stopPropagation();
          const shapeEl = (ev.currentTarget as HTMLElement).closest('.ovl--shape') as HTMLElement | null;
          const rc = shapeEl?.getBoundingClientRect();
          if (!rc) return;
          adjustRef.current = { index: i, left: rc.left, top: rc.top, width: rc.width, height: rc.height };
          try {
            (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
          } catch {
            /* best-effort */
          }
          setLiveAdjust({ index: i, adjustments: [...adj] });
        }}
        onPointerMove={(ev) => {
          const d = adjustRef.current;
          if (!d || d.index !== i || d.width === 0 || d.height === 0) return;
          const px = Math.max(0, Math.min(1, (ev.clientX - d.left) / d.width));
          const py = Math.max(0, Math.min(1, 1 - (ev.clientY - d.top) / d.height));
          const cur = liveAdjust && liveAdjust.index === i ? liveAdjust.adjustments : adj;
          setLiveAdjust({ index: i, adjustments: handle.toAdjustments(px, py, cur, bounds) });
        }}
        onPointerUp={() => {
          const lr = liveAdjust;
          const d = adjustRef.current;
          adjustRef.current = null;
          setLiveAdjust(null);
          if (d && lr && lr.index === i) updateEditAdjustments(i, lr.adjustments);
        }}
      />
    );
  }

  // CSS-space arrowhead triangle points for the line overlay (tip + 2 wings).
  function arrowPoly(tip: { x: number; y: number }, dx: number, dy: number, len: number): string {
    const d = Math.hypot(dx, dy) || 1;
    const ux = dx / d;
    const uy = dy / d;
    const aLen = Math.max(7, Math.min(len * 0.5, 14));
    const aHalf = aLen * 0.45;
    const bx = tip.x - ux * aLen;
    const by = tip.y - uy * aLen;
    const px = -uy;
    const py = ux;
    return `${tip.x},${tip.y} ${bx + px * aHalf},${by + py * aHalf} ${bx - px * aHalf},${by - py * aHalf}`;
  }

  function endLineDrag(ev: React.PointerEvent): void {
    const d = lineRef.current;
    lineRef.current = null;
    const live = liveLine;
    setLiveLine(null);
    if (!d) return;
    const movedFar = Math.abs(ev.clientX - d.startX) + Math.abs(ev.clientY - d.startY) > MOVE_PX;
    if (d.mode === 'move') {
      if (movedFar && box) {
        // CSS delta → PDF delta, rotation-aware (works on rotated pages).
        const pd = cssDeltaToPdfDelta(ev.clientX - d.startX, ev.clientY - d.startY, pageDims, box, delta);
        moveLineBy(d.index, pd.dx, pd.dy);
      } else if (ev.shiftKey || ev.metaKey || ev.ctrlKey) {
        toggleEditSelection(d.index);
      } else {
        selectEdit(d.index);
      }
      return;
    }
    // Endpoint reshape: convert the live (possibly snapped) endpoint to PDF.
    if (live && live.index === d.index) {
      const pt = d.mode === 'start' ? live.p1 : live.p2;
      updateLineEndpoint(d.index, d.mode, cssPtToPdf(pt.x, pt.y));
    }
  }

  // Body drag (the fat transparent hit line) = move the whole line / select.
  function lineBodyProps(i: number, p1: { x: number; y: number }, p2: { x: number; y: number }): Record<string, unknown> {
    if (!interactive) return {};
    return {
      onPointerDown: (ev: React.PointerEvent) => {
        ev.stopPropagation();
        try {
          (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
        } catch {
          /* best-effort */
        }
        lineRef.current = { index: i, mode: 'move', startX: ev.clientX, startY: ev.clientY, p1, p2 };
        setLiveLine({ index: i, p1, p2 });
      },
      onPointerMove: (ev: React.PointerEvent) => {
        const d = lineRef.current;
        if (!d || d.mode !== 'move') return;
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        setLiveLine({ index: i, p1: { x: d.p1.x + dx, y: d.p1.y + dy }, p2: { x: d.p2.x + dx, y: d.p2.y + dy } });
      },
      onPointerUp: endLineDrag,
    };
  }

  // Endpoint handle drag = reshape one end (Shift snaps to 0/45/90°).
  function endpointProps(i: number, which: 'start' | 'end', p1: { x: number; y: number }, p2: { x: number; y: number }): Record<string, unknown> {
    return {
      onPointerDown: (ev: React.PointerEvent) => {
        ev.stopPropagation();
        try {
          (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
        } catch {
          /* best-effort */
        }
        lineRef.current = { index: i, mode: which, startX: ev.clientX, startY: ev.clientY, p1, p2 };
        setLiveLine({ index: i, p1, p2 });
      },
      onPointerMove: (ev: React.PointerEvent) => {
        const d = lineRef.current;
        if (!d || (d.mode !== 'start' && d.mode !== 'end') || !box) return;
        const rect = box;
        void rect;
        // Pointer position in overlay-CSS space.
        const host = (ev.currentTarget as HTMLElement).closest('.page__overlay') as HTMLElement | null;
        const hr = host?.getBoundingClientRect();
        if (!hr) return;
        let cx = ev.clientX - hr.left;
        let cy = ev.clientY - hr.top;
        const anchor = d.mode === 'start' ? d.p2 : d.p1;
        if (ev.shiftKey) {
          // Snap the dragged endpoint to 0/45/90° relative to the fixed anchor.
          const adx = cx - anchor.x;
          const ady = cy - anchor.y;
          const ang = Math.round(Math.atan2(ady, adx) / (Math.PI / 4)) * (Math.PI / 4);
          const r = Math.hypot(adx, ady);
          cx = anchor.x + Math.cos(ang) * r;
          cy = anchor.y + Math.sin(ang) * r;
        }
        const moved = d.mode === 'start' ? { p1: { x: cx, y: cy }, p2: d.p2 } : { p1: d.p1, p2: { x: cx, y: cy } };
        setLiveLine({ index: i, ...moved });
      },
      onPointerUp: endLineDrag,
    };
  }

  function commitEdit(value: string): void {
    if (editing) updateEditText(editing.index, value);
    setEditing(null);
  }

  // ✎ badge: opens an inline editor for the overlay's text/note content. Its own
  // pointer handler stops propagation so it never triggers the overlay drag/delete.
  function editBadge(
    i: number,
    value: string,
    r: { left: number; top: number; width: number; height: number },
    fontPx: number,
  ): React.ReactElement | null {
    if (!interactive) return null;
    return (
      <button
        type="button"
        className="ovl__edit"
        title="内容を編集"
        onPointerDown={(ev) => ev.stopPropagation()}
        onClick={(ev) => {
          ev.stopPropagation();
          setEditing({ index: i, value, css: { left: r.left, top: r.top, width: Math.max(80, r.width), fontPx } });
        }}
      >
        ✎
      </button>
    );
  }

  // The ✕ delete button — the ONLY way to delete a placed item. Its pointer
  // handler stops propagation so it never starts an overlay drag.
  function deleteBadge(i: number): React.ReactElement | null {
    if (!interactive) return null;
    return (
      <button
        type="button"
        className="ovl__del"
        title="削除"
        aria-label="削除"
        onPointerDown={(ev) => ev.stopPropagation()}
        onClick={(ev) => {
          ev.stopPropagation();
          removeEdit(i);
        }}
      >
        ✕
      </button>
    );
  }

  return (
    <div className={`page__overlay ${interactive ? 'page__overlay--interactive' : ''}`.trim()} aria-hidden={!interactive}>
      {edits.flatMap((e, i) => {
        if (!('page' in e) || e.page !== pageIndex) return [];
        if (e.kind === 'add-annotation') {
          const r = toCss({ x: e.x, y: e.y, width: e.width, height: e.height });
          const colorStyle = {
            ...(e.color
              ? { background: rgbCss(e.color, 0.3), borderColor: rgbCss(e.color), color: rgbCss(e.color) }
              : {}),
            ...(e.opacity !== undefined ? { opacity: e.opacity } : {}),
          };
          return [
            <div
              key={i}
              className="ovl ovl--note"
              // The annotation rect anchors the note, but a sticky comment must be
              // READABLE: size to fit the text (min the placed rect) so the comment
              // is shown, not just a marker icon. The saved PDF keeps e.width/height.
              style={{
                ...positionStyle(r),
                ...colorStyle,
                ...dragStyle(i),
                width: Math.max(r.width, 150),
                height: 'auto',
                minHeight: r.height,
              }}
              {...itemProps(i, e.note, e.x, e.y)}
            >
              <span className="ovl__note-icon" aria-hidden="true">✎</span>
              <span className="ovl__note-body">{e.note || '（空のコメント）'}</span>
              {deleteBadge(i)}
              {editBadge(i, e.note, r, 13)}
            </div>,
          ];
        }
        if (e.kind === 'add-highlight') {
          const r = toCss({ x: e.x, y: e.y, width: e.width, height: e.height });
          const colorStyle = {
            ...(e.color ? { background: rgbCss(e.color, 0.45) } : {}),
            ...(e.opacity !== undefined ? { opacity: e.opacity } : {}),
          };
          return [
            <div
              key={i}
              className="ovl ovl--hl"
              style={{ ...positionStyle(r), ...colorStyle, ...dragStyle(i), ...resizeStyle(i, r) }}
              {...itemProps(i, e.note ?? 'ハイライト', e.x, e.y)}
            >
              {deleteBadge(i)}
              {editBadge(i, e.note ?? '', r, 13)}
              {resizeHandle(i, r)}
            </div>,
          ];
        }
        if (e.kind === 'add-text') {
          // Text uses (x, y) as the baseline; render a small marker + the text.
          const r = toCss({
            x: e.x,
            y: e.y,
            width: estimateTextWidth(e.text, e.size),
            height: e.size,
          });
          return [
            <div
              key={i}
              className="ovl ovl--text"
              style={{ ...positionStyle(r), fontSize: r.height, ...dragStyle(i) }}
              {...itemProps(i, e.text, e.x, e.y)}
            >
              {e.text}
              {deleteBadge(i)}
              {editBadge(i, e.text, r, r.height)}
            </div>,
          ];
        }
        if (e.kind === 'add-image') {
          const r = toCss({ x: e.x, y: e.y, width: e.width, height: e.height });
          return [
            <ImageOverlay
              key={i}
              bytes={e.imageBytes}
              style={{ ...positionStyle(r), ...dragStyle(i), ...resizeStyle(i, r) }}
              {...itemProps(i, '画像', e.x, e.y)}
            >
              {deleteBadge(i)}
              {resizeHandle(i, r)}
            </ImageOverlay>,
          ];
        }
        if (e.kind === 'add-shape') {
          const r = toCss({ x: e.x, y: e.y, width: e.width, height: e.height });
          // Live adjustment while dragging a ◆ handle, else the stored values.
          const adj = liveAdjust && liveAdjust.index === i ? liveAdjust.adjustments : e.adjustments;
          const bounds = { x: e.x, y: e.y, width: e.width, height: e.height };
          // Preview the same geometry the driver will draw, stretched to the placed
          // box. Freehand has no generator → render its stored path directly.
          const d =
            e.shapeType === FREEHAND_SHAPE_TYPE
              ? freehandSvgD(e.path, bounds, 100)
              : shapeSvgPathD(e.shapeType, 100, adj);
          const adjustSpec = shapeAdjustSpec(e.shapeType);
          // Live rotation while dragging the ⟳ handle, else the stored value.
          const rot = liveRotate && liveRotate.index === i ? liveRotate.rotation : e.rotation ?? 0;
          // Live translate from a single drag OR a group move (keeps the shape
          // itself visible while moving, not just its outline).
          const md = liveMoveDelta(i);
          const ld = md ? `translate(${md.dx}px, ${md.dy}px) ` : '';
          const active = !!md || liveResize?.index === i || liveRotate?.index === i;
          return [
            <div
              key={i}
              className={`ovl ovl--shape ${selectedEditIndices.includes(i) ? 'ovl--selected' : ''}`.trim()}
              style={{
                ...positionStyle(r),
                ...resizeStyle(i, r),
                // rotate about the box center; translate (live drag) is applied
                // first so the rotation pivots around the dragged position too.
                transform: `${ld}rotate(${rot}deg)`,
                transformOrigin: 'center center',
                ...(active ? { zIndex: 20 } : {}),
                ...(ld ? { cursor: 'grabbing' } : {}),
              }}
              {...itemProps(i, e.shapeType, e.x, e.y)}
            >
              {d && (
                <svg
                  className="ovl__shape-svg"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d={d}
                    fill={e.fill ? rgbCss(e.fill, e.opacity ?? 1) : 'none'}
                    stroke={e.stroke ? rgbCss(e.stroke, e.opacity ?? 1) : 'none'}
                    strokeWidth={e.strokeWidth ?? 1}
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
              {deleteBadge(i)}
              {/* Resize is gated to the unrotated state for now; rotate freely. */}
              {rot === 0 ? resizeHandle(i, r) : null}
              {rotateHandle(i, rot)}
              {/* PowerPoint adjustment handles (◆) — unrotated frame only. */}
              {rot === 0 && adjustSpec
                ? adjustSpec.handles.map((h, hi) => adjustHandle(i, hi, h, adj ?? adjustSpec.defaults, bounds))
                : null}
            </div>,
          ];
        }
        if (e.kind === 'add-line') {
          const live = liveLine && liveLine.index === i ? liveLine : null;
          const p1 = live ? live.p1 : toCssPt(e.x1, e.y1);
          const p2 = live ? live.p2 : toCssPt(e.x2, e.y2);
          const cssW = Math.max(1, e.strokeWidth * sxScale);
          const color = rgbCss(e.stroke, e.opacity ?? 1);
          const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
          const sel = selectedEditIndices.includes(i);
          const editable = interactive && !multi;
          const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
          // Group move: translate the whole line wrapper live (single-line move
          // already follows via liveLine endpoints above).
          const lmd = liveMoveDelta(i);
          return [
            <div
              key={i}
              className="ovl-line"
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'visible',
                ...(lmd ? { transform: `translate(${lmd.dx}px, ${lmd.dy}px)` } : {}),
              }}
            >
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }} aria-hidden="true">
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={color} strokeWidth={cssW} strokeLinecap="round" />
                {e.endArrow && <polygon points={arrowPoly(p2, p2.x - p1.x, p2.y - p1.y, len)} fill={color} />}
                {e.startArrow && <polygon points={arrowPoly(p1, p1.x - p2.x, p1.y - p2.y, len)} fill={color} />}
                {editable && (
                  // Fat transparent hit-line: body drag = move / click = select.
                  <line
                    x1={p1.x}
                    y1={p1.y}
                    x2={p2.x}
                    y2={p2.y}
                    stroke="transparent"
                    strokeWidth={Math.max(14, cssW + 8)}
                    strokeLinecap="round"
                    style={{ pointerEvents: 'stroke', cursor: 'move' }}
                    {...lineBodyProps(i, p1, p2)}
                  />
                )}
              </svg>
              {editable && sel && (
                <>
                  <button
                    type="button"
                    className="ovl__del ovl-line__del"
                    title="削除"
                    aria-label="削除"
                    style={{ position: 'absolute', left: `${mid.x}px`, top: `${mid.y}px` }}
                    onPointerDown={(ev) => ev.stopPropagation()}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      removeEdit(i);
                    }}
                  >
                    ✕
                  </button>
                  <span className="ovl-line__end" style={{ left: `${p1.x}px`, top: `${p1.y}px` }} {...endpointProps(i, 'start', p1, p2)} />
                  <span className="ovl-line__end" style={{ left: `${p2.x}px`, top: `${p2.y}px` }} {...endpointProps(i, 'end', p1, p2)} />
                </>
              )}
            </div>,
          ];
        }
        if (e.kind === 'add-text-markup') {
          // Display-only overlay of a semantic /Highlight, /Underline, or
          // /StrikeOut. The saved bytes come from the driver's real annotation;
          // here we just preview it (highlight = translucent fill, underline /
          // strikeout = a rule per run rect). The whole markup moves/selects as
          // one object (one PDF annotation), like add-highlight.
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          for (const rr of e.rects) {
            minX = Math.min(minX, rr.x);
            minY = Math.min(minY, rr.y);
            maxX = Math.max(maxX, rr.x + rr.width);
            maxY = Math.max(maxY, rr.y + rr.height);
          }
          const bw = Math.max(1, maxX - minX);
          const bh = Math.max(1, maxY - minY);
          const r = toCss({ x: minX, y: minY, width: bw, height: bh });
          const paint = e.color
            ? rgbCss(e.color, e.opacity ?? (e.markup === 'highlight' ? 0.45 : 1))
            : rgbCss({ r: 1, g: 0.85, b: 0.2 }, e.markup === 'highlight' ? 0.45 : 1);
          return [
            <div
              key={i}
              className={`ovl ovl--hl ${selectedEditIndices.includes(i) ? 'ovl--selected' : ''}`.trim()}
              style={{ ...positionStyle(r), background: 'transparent', ...dragStyle(i) }}
              {...itemProps(i, e.note ?? '注釈', minX, maxY)}
            >
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
                {e.rects.map((rr, ri) => {
                  const lx = ((rr.x - minX) / bw) * 100;
                  const lw = (rr.width / bw) * 100;
                  const lyTop = ((maxY - (rr.y + rr.height)) / bh) * 100;
                  const lh = (rr.height / bh) * 100;
                  if (e.markup === 'highlight') {
                    return <rect key={ri} x={lx} y={lyTop} width={lw} height={lh} fill={paint} />;
                  }
                  const ly = e.markup === 'underline' ? lyTop + lh : lyTop + lh / 2;
                  return (
                    <line key={ri} x1={lx} y1={ly} x2={lx + lw} y2={ly} stroke={paint} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
                  );
                })}
              </svg>
              {deleteBadge(i)}
            </div>,
          ];
        }
        if (e.kind === 'add-ink') {
          // Display-only overlay of a semantic /Ink annotation. The strokes are
          // drawn as polylines; the whole ink moves/selects as one object.
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          for (const stroke of e.strokes) {
            for (const p of stroke) {
              minX = Math.min(minX, p.x);
              minY = Math.min(minY, p.y);
              maxX = Math.max(maxX, p.x);
              maxY = Math.max(maxY, p.y);
            }
          }
          const bw = Math.max(1, maxX - minX);
          const bh = Math.max(1, maxY - minY);
          const r = toCss({ x: minX, y: minY, width: bw, height: bh });
          const col = rgbCss(e.stroke, e.opacity ?? 1);
          const bounds = { x: minX, y: minY, width: bw, height: bh };
          // Live move + rotate (ink bakes rotation into points on commit, so the
          // resting rotation is always 0 — only the drag shows a CSS rotate).
          const md = liveMoveDelta(i);
          const ld = md ? `translate(${md.dx}px, ${md.dy}px) ` : '';
          const inkRot = liveRotate && liveRotate.index === i ? liveRotate.rotation : 0;
          const active = !!md || liveResize?.index === i || liveRotate?.index === i;
          return [
            <div
              key={i}
              className={`ovl ovl--shape ${selectedEditIndices.includes(i) ? 'ovl--selected' : ''}`.trim()}
              style={{
                ...positionStyle(r),
                ...resizeStyle(i, r),
                transform: `${ld}rotate(${inkRot}deg)`,
                transformOrigin: 'center center',
                ...(active ? { zIndex: 20 } : {}),
                ...(ld ? { cursor: 'grabbing' } : {}),
              }}
              {...itemProps(i, '手書き', minX, maxY)}
            >
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
                {e.strokes.map((stroke, si) => (
                  <polyline
                    key={si}
                    points={inkStrokeSvgPoints(stroke, bounds, 100)}
                    fill="none"
                    stroke={col}
                    strokeWidth={Math.max(1, e.strokeWidth)}
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>
              {deleteBadge(i)}
              {inkRot === 0 ? resizeHandle(i, r) : null}
              {rotateHandle(i, inkRot)}
            </div>,
          ];
        }
        return [];
      })}
      {multi &&
        (() => {
          // Group bounding box for the items selected ON THIS PAGE. The box +
          // move work at any rotation; the scale handle is unrotated-only.
          const sel = selectedEditIndices.filter((i) => {
            const e = edits[i];
            return !!e && 'page' in e && e.page === pageIndex && !!editPdfBounds(e);
          });
          if (sel.length < 2) return null;
          let cl = Infinity,
            ct = Infinity,
            cr = -Infinity,
            cb = -Infinity;
          let px = Infinity,
            py = Infinity,
            pr = -Infinity,
            pbtm = -Infinity;
          for (const i of sel) {
            const pb = editPdfBounds(edits[i] as Edit);
            if (!pb) continue;
            const c = toCss(pb);
            cl = Math.min(cl, c.left);
            ct = Math.min(ct, c.top);
            cr = Math.max(cr, c.left + c.width);
            cb = Math.max(cb, c.top + c.height);
            px = Math.min(px, pb.x);
            py = Math.min(py, pb.y);
            pr = Math.max(pr, pb.x + pb.width);
            pbtm = Math.max(pbtm, pb.y + pb.height);
          }
          const cssBox = { left: cl, top: ct, width: cr - cl, height: cb - ct };
          const pdfBox = { x: px, y: py, width: pr - px, height: pbtm - py };
          const style: React.CSSProperties = { position: 'absolute', left: `${cl}px`, top: `${ct}px`, width: `${cssBox.width}px`, height: `${cssBox.height}px` };
          if (groupLive?.mode === 'move') style.transform = `translate(${groupLive.dx}px, ${groupLive.dy}px)`;
          else if (groupLive?.mode === 'scale') {
            style.width = `${Math.max(8, cssBox.width + groupLive.dx)}px`;
            style.height = `${Math.max(8, cssBox.height + groupLive.dy)}px`;
          }
          return (
            <div className="ovl-group" style={style}>
              <div className="ovl-group__body" title="ドラッグでまとめて移動" {...groupHandleProps('move', cssBox, pdfBox)} />
              <button
                type="button"
                className="ovl__del ovl-group__del"
                title="まとめて削除"
                aria-label="選択をまとめて削除"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  removeEdits(selectedEditIndices);
                }}
              >
                ✕
              </button>
              {delta === 0 && (
                <span className="ovl-group__resize" title="ドラッグでまとめて拡大縮小" {...groupHandleProps('scale', cssBox, pdfBox)} />
              )}
            </div>
          );
        })()}
      {searchHighlight && searchHighlight.pageIndex === pageIndex
        ? searchHighlight.rects.map((r, idx) => (
            <div
              key={`sh-${idx}`}
              className="ovl ovl--search"
              style={{ ...positionStyle(toCss(r)), pointerEvents: 'none' }}
              aria-hidden="true"
            />
          ))
        : null}
      {editing && (
        <input
          className="text-draft"
          autoFocus
          defaultValue={editing.value}
          placeholder="内容を入力 (Enter)"
          style={{
            left: `${editing.css.left}px`,
            top: `${editing.css.top}px`,
            width: `${editing.css.width}px`,
            fontSize: `${Math.max(8, editing.css.fontPx)}px`,
            lineHeight: `${Math.max(8, editing.css.fontPx)}px`,
            pointerEvents: 'auto',
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              commitEdit((e.target as HTMLInputElement).value);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setEditing(null);
            }
          }}
          onBlur={(e) => commitEdit(e.target.value)}
        />
      )}
    </div>
  );
}

function positionStyle(r: { left: number; top: number; width: number; height: number }): React.CSSProperties {
  return {
    position: 'absolute',
    left: `${r.left}px`,
    top: `${r.top}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
  };
}

/** True for East-Asian full-width code points (CJK, kana, Hangul, fullwidth forms). */
function isWideChar(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x115f) || // Hangul Jamo
    (cp >= 0x2e80 && cp <= 0x303e) || // CJK radicals / Kangxi / CJK punctuation
    (cp >= 0x3041 && cp <= 0x33ff) || // Hiragana, Katakana, CJK symbols
    (cp >= 0x3400 && cp <= 0x4dbf) || // CJK Ext A
    (cp >= 0x4e00 && cp <= 0x9fff) || // CJK Unified Ideographs
    (cp >= 0xa000 && cp <= 0xa4cf) || // Yi
    (cp >= 0xac00 && cp <= 0xd7a3) || // Hangul syllables
    (cp >= 0xf900 && cp <= 0xfaff) || // CJK compatibility ideographs
    (cp >= 0xfe30 && cp <= 0xfe4f) || // CJK compatibility forms
    (cp >= 0xff00 && cp <= 0xff60) || // Fullwidth forms
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x20000 && cp <= 0x3fffd) // CJK Ext B+
  );
}

/**
 * Estimate the on-screen width (in PDF points) of a text run for the placement
 * overlay box. Full-width CJK glyphs advance ~1em; Latin ~0.55em. This only
 * sizes the live preview box — the saved PDF uses the driver's real glyph
 * metrics — but a Latin-only estimate makes the CJK box too narrow, so the
 * overlay text wraps and overlaps. Per-char widths keep it a single clean line.
 */
function estimateTextWidth(text: string, size: number): number {
  let w = 0;
  for (const ch of text) {
    w += isWideChar(ch.codePointAt(0) ?? 0) ? size : size * 0.55;
  }
  return Math.max(4, w);
}

/** Spatial PDF bbox of an edit (null for non-spatial edits like page ops). */
function editPdfBounds(ed: Edit): { x: number; y: number; width: number; height: number } | null {
  switch (ed.kind) {
    case 'add-shape':
    case 'add-image':
    case 'add-highlight':
    case 'add-annotation':
      return { x: ed.x, y: ed.y, width: ed.width, height: ed.height };
    case 'add-text':
      return { x: ed.x, y: ed.y, width: estimateTextWidth(ed.text, ed.size), height: ed.size };
    case 'add-line':
      return {
        x: Math.min(ed.x1, ed.x2),
        y: Math.min(ed.y1, ed.y2),
        width: Math.abs(ed.x1 - ed.x2),
        height: Math.abs(ed.y1 - ed.y2),
      };
    default:
      return null;
  }
}

// Breathing room (px) subtracted from the measured container before fit math,
// so a fit-width page doesn't kiss the scrollbar / gutter.
const FIT_GUTTER = 12;

export function PdfViewer() {
  const originalBytes = useEditorStore((s) => s.originalBytes);
  const pages = useEditorStore((s) => s.doc?.pages ?? []);
  const edits = useEditorStore((s) => s.edits);
  const activeTool = useEditorStore((s) => s.activeTool);
  const activeShapeType = useEditorStore((s) => s.activeShapeType);
  const activeLineTool = useEditorStore((s) => s.activeLineTool);
  const pendingImage = useEditorStore((s) => s.pendingImage);
  const activeMarkup = useEditorStore((s) => s.activeMarkup);
  const setActiveMarkup = useEditorStore((s) => s.setActiveMarkup);
  const addEdit = useEditorStore((s) => s.addEdit);
  const removeEdit = useEditorStore((s) => s.removeEdit);
  const rotations = effectiveRotations(pages, edits);
  const pageCount = pages.length;
  const order = effectiveOrder(pageCount, edits);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Zoom: mode drives a derived scale (fit-*), or `custom` uses customScale.
  const [zoomMode, setZoomMode] = useState<ZoomMode>('fit-width');
  const [customScale, setCustomScale] = useState(1);
  // Selected color + opacity for new highlight / note placements.
  const [annoColor, setAnnoColor] = useState<Rgb>(COLOR_PRESETS[0]!.rgb);
  const [annoOpacity, setAnnoOpacity] = useState(1);
  const colorTool = activeTool === 'highlight' || activeTool === 'note' || activeTool === 'markup';

  // Shape style: the default for NEW shapes, and (when a shape is selected) the
  // editing target for the contextual style bar.
  const selectedEditIndices = useEditorStore((s) => s.selectedEditIndices);
  const updateEditStyle = useEditorStore((s) => s.updateEditStyle);
  const smoothEdit = useEditorStore((s) => s.smoothEdit);
  const alignEdits = useEditorStore((s) => s.alignEdits);
  const distributeEdits = useEditorStore((s) => s.distributeEdits);
  // The style bar targets a single selection; with ≥2 selected the group box
  // (in the overlay) owns bulk move/scale/delete instead.
  const singleSelIndex = selectedEditIndices.length === 1 ? selectedEditIndices[0]! : null;
  const [shapeStyle, setShapeStyle] = useState<ShapeStyle>({
    fill: DEFAULT_SHAPE_FILL,
    stroke: DEFAULT_SHAPE_STROKE,
    strokeWidth: DEFAULT_SHAPE_STROKE_WIDTH,
    opacity: 1,
  });
  // Arming a mark (✓ ✗ ○ ● ◎) seeds its own default paint — outline marks are
  // stroked with no fill, ● is a solid dot — so it draws right without the user
  // touching the style bar. Ordinary autoshapes keep the persisted style.
  useEffect(() => {
    if (activeShapeType && isMarkShape(activeShapeType)) {
      setShapeStyle(markDefaultStyle(activeShapeType));
    }
  }, [activeShapeType]);
  // Blob URL for the armed image's live placement preview (revoked on change).
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingImage) {
      setPendingImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(new Blob([pendingImage.bytes as BlobPart]));
    setPendingImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImage]);
  const selEdit = singleSelIndex != null ? edits[singleSelIndex] : undefined;
  const selectedShape = selEdit && selEdit.kind === 'add-shape' ? selEdit : null;
  const selectedLine = selEdit && selEdit.kind === 'add-line' ? selEdit : null;
  // Ink (freehand /Ink) is an open stroke with no fill — style it like a line.
  const selectedInk = selEdit && selEdit.kind === 'add-ink' ? selEdit : null;
  // add-shape freehand also has no fill; the smooth action targets it.
  const freehandSelected = !!selectedShape && selectedShape.shapeType === FREEHAND_SHAPE_TYPE;
  // The style bar serves shapes (fill + stroke + width + opacity) and stroked
  // items — lines AND ink (stroke + width + opacity only). It edits the selected
  // item, or — with no selection while a placement tool is armed — the next item.
  const styleMode: 'shape' | 'line' | null = selectedShape
    ? 'shape'
    : selectedLine || selectedInk
      ? 'line'
      : activeTool === 'shape'
        ? 'shape'
        : activeTool === 'line' || activeTool === 'freehand'
          ? 'line'
          : null;
  const showShapeStyle = styleMode !== null;
  const isLineStyle = styleMode === 'line';
  const curFill: Rgb | null = isLineStyle ? null : selectedShape ? selectedShape.fill ?? null : shapeStyle.fill;
  const curStroke: Rgb | null = isLineStyle
    ? selectedLine
      ? selectedLine.stroke
      : selectedInk
        ? selectedInk.stroke
        : shapeStyle.stroke ?? DEFAULT_SHAPE_STROKE
    : selectedShape
      ? selectedShape.stroke ?? null
      : shapeStyle.stroke;
  const curWidth = selectedShape
    ? selectedShape.strokeWidth ?? 1
    : selectedLine
      ? selectedLine.strokeWidth
      : selectedInk
        ? selectedInk.strokeWidth
        : shapeStyle.strokeWidth;
  const curOpacity = selectedShape
    ? selectedShape.opacity ?? 1
    : selectedLine
      ? selectedLine.opacity ?? 1
      : selectedInk
        ? selectedInk.opacity ?? 1
        : shapeStyle.opacity;
  // Apply a style patch to the selected shape / line / ink, or (no selection) to
  // the default used for the next placement.
  const applyShapeStyle = (patch: { fill?: Rgb | null; stroke?: Rgb | null; strokeWidth?: number; opacity?: number }) => {
    if ((selectedShape || selectedLine || selectedInk) && singleSelIndex != null) {
      updateEditStyle(singleSelIndex, patch);
    } else {
      setShapeStyle((s) => ({
        ...s,
        ...('fill' in patch ? { fill: patch.fill ?? null } : {}),
        ...('stroke' in patch ? { stroke: patch.stroke ?? null } : {}),
        ...(patch.strokeWidth !== undefined ? { strokeWidth: patch.strokeWidth } : {}),
        ...(patch.opacity !== undefined ? { opacity: patch.opacity } : {}),
      }));
    }
  };
  // Available content box (px), measured from the viewer column.
  const [avail, setAvail] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [pvEl, setPvEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pvEl) return;
    const measure = () => {
      const parent = pvEl.parentElement; // the .viewer scroll container
      setAvail({
        w: Math.max(0, pvEl.clientWidth - FIT_GUTTER),
        h: Math.max(0, (parent?.clientHeight ?? pvEl.clientHeight) - FIT_GUTTER),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(pvEl);
    if (pvEl.parentElement) ro.observe(pvEl.parentElement);
    return () => ro.disconnect();
  }, [pvEl]);

  useEffect(() => {
    if (!originalBytes) {
      setDoc(null);
      return;
    }
    let active = true;
    let loaded: PDFDocumentProxy | null = null;
    setError(null);
    loadViewerDocument(originalBytes)
      .then((d) => {
        if (active) {
          loaded = d;
          setDoc(d);
        } else {
          void d.destroy();
        }
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'プレビューに失敗しました');
      });
    return () => {
      active = false;
      void loaded?.destroy();
    };
  }, [originalBytes]);

  // Largest displayed page dims (rotation-aware) so fit-width fits every page.
  const maxDisp = pages.reduce(
    (acc, p) => {
      const rot = rotations.get(p.index) ?? p.rotation;
      const swapped = rot === 90 || rot === 270;
      const w = swapped ? p.height : p.width;
      const h = swapped ? p.width : p.height;
      return { w: Math.max(acc.w, w), h: Math.max(acc.h, h) };
    },
    { w: 0, h: 0 },
  );

  const fitScale =
    zoomMode === 'fit-page'
      ? fitPageScale(maxDisp.w, maxDisp.h, avail.w, avail.h)
      : fitWidthScale(maxDisp.w, avail.w);
  const scale = zoomMode === 'custom' ? customScale : fitScale;

  // +/- step from the CURRENT effective scale, then pin to custom mode.
  const stepZoom = (dir: 'in' | 'out') => {
    const base = scale;
    setCustomScale(dir === 'in' ? nextZoomIn(base) : nextZoomOut(base));
    setZoomMode('custom');
  };

  // Keyboard zoom: Ctrl/Cmd + = / - / 0. Separate from the undo/redo listener
  // in edit.tsx (different keys, no conflict). Skip when typing in an input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        stepZoom('in');
      } else if (e.key === '-') {
        e.preventDefault();
        stepZoom('out');
      } else if (e.key === '0') {
        e.preventDefault();
        setZoomMode('fit-width');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // stepZoom closes over `scale`; re-bind when it changes so steps compound.
  }, [scale]);

  if (error) return <p style={{ color: 'var(--danger)' }}>プレビュー失敗: {error}</p>;
  if (!doc) return <p style={{ color: 'var(--muted)' }}>レンダリング中…</p>;

  return (
    <div className="pv" ref={setPvEl}>
      <div className="pv__zoombar" role="toolbar" aria-label="ズーム">
        <button
          className={`zoombtn ${zoomMode === 'fit-width' ? 'zoombtn--on' : ''}`.trim()}
          onClick={() => setZoomMode('fit-width')}
          title="幅に合わせる (Ctrl/Cmd+0)"
        >
          幅
        </button>
        <button
          className={`zoombtn ${zoomMode === 'fit-page' ? 'zoombtn--on' : ''}`.trim()}
          onClick={() => setZoomMode('fit-page')}
          title="ページ全体"
        >
          全体
        </button>
        <span className="pv__zoomsep" />
        <button className="zoombtn" onClick={() => stepZoom('out')} title="縮小 (Ctrl/Cmd+-)" aria-label="縮小">
          −
        </button>
        <span className="pv__zoomval" aria-live="polite">
          {zoomLabel(scale)}
        </span>
        <button className="zoombtn" onClick={() => stepZoom('in')} title="拡大 (Ctrl/Cmd+=)" aria-label="拡大">
          ＋
        </button>
        {colorTool && (
          <>
            <span className="pv__zoomsep" />
            <span className="pv__palette" role="group" aria-label="注釈の色">
              {COLOR_PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  className={`swatch ${sameRgb(p.rgb, annoColor) ? 'swatch--on' : ''}`.trim()}
                  style={{ background: rgbCss(p.rgb) }}
                  title={p.name}
                  aria-label={p.name}
                  aria-pressed={sameRgb(p.rgb, annoColor)}
                  onClick={() => setAnnoColor(p.rgb)}
                />
              ))}
              <label
                className={`swatch swatch--custom ${!isPreset(annoColor) ? 'swatch--on' : ''}`.trim()}
                title="カスタムカラー"
                style={!isPreset(annoColor) ? { background: rgbCss(annoColor) } : undefined}
              >
                <input
                  type="color"
                  value={rgbToHex(annoColor)}
                  onChange={(e) => setAnnoColor(hexToRgb(e.target.value))}
                  aria-label="カスタムカラー"
                />
              </label>
              <label className="pv__opacity" title="不透明度">
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={annoOpacity}
                  onChange={(e) => setAnnoOpacity(Number(e.target.value))}
                  aria-label="不透明度"
                />
                <span className="pv__opacity-val">{Math.round(annoOpacity * 100)}%</span>
              </label>
            </span>
          </>
        )}
        {activeTool === 'markup' && (
          <>
            <span className="pv__zoomsep" />
            <span className="pv__markup" role="group" aria-label="テキストマークアップ">
              {([
                ['highlight', 'マーカー'],
                ['underline', '下線'],
                ['strikeout', '取消線'],
              ] as const).map(([kind, label]) => (
                <button
                  key={kind}
                  type="button"
                  className={`pv__markup-btn ${activeMarkup === kind ? 'pv__markup-btn--on' : ''}`.trim()}
                  aria-pressed={activeMarkup === kind}
                  onClick={() => setActiveMarkup(kind)}
                >
                  {label}
                </button>
              ))}
              <span className="pv__markup-hint">テキストをなぞって選択</span>
            </span>
          </>
        )}
        {showShapeStyle && (
          <>
            <span className="pv__zoomsep" />
            <span className="pv__shape-style" role="group" aria-label={selEdit ? '選択中のスタイル' : 'スタイル'}>
              {!isLineStyle && !freehandSelected && (
                <ColorRow label="塗り" value={curFill} onChange={(c) => applyShapeStyle({ fill: c })} />
              )}
              <ColorRow label="線" value={curStroke} onChange={(c) => applyShapeStyle({ stroke: c })} />
              <label className="pv__opacity" title="線の太さ (pt)">
                <span className="pv__style-label">太さ</span>
                <input
                  type="range"
                  min={0}
                  max={8}
                  step={0.5}
                  value={curWidth}
                  onChange={(e) => applyShapeStyle({ strokeWidth: Number(e.target.value) })}
                  aria-label="線の太さ"
                />
                <span className="pv__opacity-val">{curWidth}</span>
              </label>
              <label className="pv__opacity" title="不透明度">
                <span className="pv__style-label">不透明</span>
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={curOpacity}
                  onChange={(e) => applyShapeStyle({ opacity: Number(e.target.value) })}
                  aria-label="不透明度"
                />
                <span className="pv__opacity-val">{Math.round(curOpacity * 100)}%</span>
              </label>
              {freehandSelected && singleSelIndex != null && (
                <button
                  type="button"
                  className="alignbtn"
                  title="手書きをなめらかにする"
                  aria-label="手書きをなめらかにする"
                  onClick={() => smoothEdit(singleSelIndex)}
                >
                  スムーズ
                </button>
              )}
            </span>
          </>
        )}
        {selectedEditIndices.length >= 2 && (
          <>
            <span className="pv__zoomsep" />
            <span className="pv__align" role="group" aria-label="整列と分布">
              <button type="button" className="alignbtn" title="左揃え" aria-label="左揃え" onClick={() => alignEdits(selectedEditIndices, 'left')}>
                ⤝
              </button>
              <button type="button" className="alignbtn" title="左右中央揃え" aria-label="左右中央揃え" onClick={() => alignEdits(selectedEditIndices, 'centerX')}>
                ↔
              </button>
              <button type="button" className="alignbtn" title="右揃え" aria-label="右揃え" onClick={() => alignEdits(selectedEditIndices, 'right')}>
                ⤞
              </button>
              <span className="pv__align-sep" />
              <button type="button" className="alignbtn" title="上揃え" aria-label="上揃え" onClick={() => alignEdits(selectedEditIndices, 'top')}>
                ⤒
              </button>
              <button type="button" className="alignbtn" title="上下中央揃え" aria-label="上下中央揃え" onClick={() => alignEdits(selectedEditIndices, 'middleY')}>
                ↕
              </button>
              <button type="button" className="alignbtn" title="下揃え" aria-label="下揃え" onClick={() => alignEdits(selectedEditIndices, 'bottom')}>
                ⤓
              </button>
              {selectedEditIndices.length >= 3 && (
                <>
                  <span className="pv__align-sep" />
                  <button type="button" className="alignbtn" title="左右に等間隔分布" aria-label="左右に分布" onClick={() => distributeEdits(selectedEditIndices, 'x')}>
                    ⇔
                  </button>
                  <button type="button" className="alignbtn" title="上下に等間隔分布" aria-label="上下に分布" onClick={() => distributeEdits(selectedEditIndices, 'y')}>
                    ⇕
                  </button>
                </>
              )}
            </span>
          </>
        )}
      </div>
      {order.map((origIdx, pos) => {
        const p = pages[origIdx];
        if (!p) return null;
        const rotation = rotations.get(p.index) ?? p.rotation;
        return (
          <PageView
            key={p.index}
            pdfDoc={doc}
            page={p}
            rotation={rotation}
            scale={scale}
            effectivePos={pos}
            edits={edits}
            activeTool={activeTool}
            activeShapeType={activeShapeType}
            activeLineTool={activeLineTool}
            annoColor={annoColor}
            annoOpacity={annoOpacity}
            shapeStyle={shapeStyle}
            pendingImage={pendingImage}
            pendingImageUrl={pendingImageUrl}
            addEdit={addEdit}
            removeEdit={removeEdit}
            pageCount={pageCount}
          />
        );
      })}
    </div>
  );
}

/**
 * A placed image's on-page overlay. Renders the actual raster (via a blob URL,
 * revoked on unmount / bytes change) stretched to the placed bounds — exactly
 * what the saved PDF draws — so the page is WYSIWYG instead of a generic icon.
 * Drag / select / resize props are forwarded to the box via `...rest`.
 */
function ImageOverlay({
  bytes,
  style,
  children,
  ...rest
}: {
  bytes: Uint8Array;
  style: React.CSSProperties;
  children?: React.ReactNode;
} & Record<string, unknown>) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(new Blob([bytes as BlobPart]));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [bytes]);
  return (
    <div className="ovl ovl--image" style={style} {...rest}>
      {url ? <img className="ovl__image-img" src={url} alt="" aria-hidden="true" /> : '🖼'}
      {children}
    </div>
  );
}

/**
 * One rendered page: canvas + click handler that converts a click into the
 * appropriate add-* edit based on the active tool, plus the pending-edit
 * overlay layer on top.
 */
function PageView({
  pdfDoc,
  page,
  rotation,
  scale,
  effectivePos,
  edits,
  activeTool,
  activeShapeType,
  activeLineTool,
  annoColor,
  annoOpacity,
  shapeStyle,
  pendingImage,
  pendingImageUrl,
  addEdit,
  removeEdit,
  pageCount,
}: {
  pdfDoc: PDFDocumentProxy;
  page: PdfPage;
  rotation: Rotation;
  scale: number;
  /** This page's position in the current display order (reorder-aware). */
  effectivePos: number;
  edits: ReadonlyArray<Edit>;
  activeTool: EditorTool;
  /** The armed PowerPoint shape prst id when activeTool === 'shape'. */
  activeShapeType: string | null;
  /** The armed line tool id when activeTool === 'line'. */
  activeLineTool: string | null;
  annoColor: Rgb;
  annoOpacity: number;
  shapeStyle: ShapeStyle;
  /** The image armed for placement (activeTool === 'image'), or null. */
  pendingImage: PendingImage | null;
  /** Blob URL of the armed image for the live drag preview, or null. */
  pendingImageUrl: string | null;
  addEdit: (e: Edit) => void;
  removeEdit: (index: number) => void;
  pageCount: number;
}) {
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  // In-progress freehand stroke (canvas-CSS points) for the live preview.
  const [freehandPts, setFreehandPts] = useState<Pt[]>([]);
  const selectEdit = useEditorStore((s) => s.selectEdit);
  const setSelection = useEditorStore((s) => s.setSelection);
  const setPendingImage = useEditorStore((s) => s.setPendingImage);
  const activeMarkup = useEditorStore((s) => s.activeMarkup);
  const markupBytes = useEditorStore((s) => s.originalBytes);
  const addEdits = useEditorStore((s) => s.addEdits);
  // Drag-to-define state. `start` and `current` are in canvas-CSS coords
  // (relative to canvas top-left). Null means no drag in progress.
  const [drag, setDrag] = useState<{ start: { x: number; y: number }; current: { x: number; y: number } } | null>(null);
  // Inline editor: a positioned input over the canvas, replacing window.prompt.
  // `mode:'text'` places add-text (font-bound); `mode:'note'` types a sticky
  // note's comment. `css` positions the input over the canvas.
  type DraftCss = { left: number; top: number; w: number; fontPx: number };
  const [inlineDraft, setInlineDraft] = useState<
    | { mode: 'text'; css: DraftCss; text: { x: number; y: number; size: number; fontRef: PdfPage['fonts'][number]['ref'] } }
    | { mode: 'note'; css: DraftCss; note: { x: number; y: number; width: number; height: number; color: Rgb; opacity?: number } }
    | null
  >(null);

  // Placement works at every page rotation now — the pointer handlers map CSS
  // coords back to the page's original frame via the rotation-aware transforms.
  const placementReady = activeTool !== 'select';
  // Effective extra rotation vs. the page's base /Rotate (0/90/180/270), and the
  // page dims in points — shared by every CSS↔PDF transform in this card.
  const delta = ((((rotation - page.rotation) % 360) + 360) % 360) as RotationDelta;
  const pageDims = { widthPt: page.width, heightPt: page.height };

  /**
   * Drag-to-define: highlight and text both want the user to draw a rectangle
   * of arbitrary position and size. Notes use the start point as a click
   * (sticky notes are point anchors). A drag distance under DRAG_THRESHOLD_PX
   * counts as a click and falls back to a default-size box.
   */
  const DRAG_THRESHOLD_PX = 4;

  function localPoint(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!canvasEl) return null;
    const rect = canvasEl.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  // Mark every text run the selection rect covers (highlight / underline /
  // strikeout), snapped to pdf.js runs. Text comes from the cached extraction of
  // the original bytes; the markup edits append as one undoable batch.
  async function applyTextMarkup(
    sel: { x: number; y: number; width: number; height: number },
    kind: MarkupKind,
  ): Promise<void> {
    if (!markupBytes) return;
    const pages = await getPageTexts(markupBytes);
    const pt = pages.find((p) => p.pageIndex === page.index);
    if (!pt) return;
    const rects = runsInRect(pt.items ?? [], sel);
    if (rects.length === 0) return;
    // Markup uses the shared annotation color (the palette shows for this tool).
    addEdits(markupEditsForRuns(page.index, rects, kind, annoColor));
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    if (!placementReady && activeTool !== 'select') return;
    const p = localPoint(e.clientX, e.clientY);
    if (!p) {
      if (activeTool === 'select') selectEdit(null);
      return;
    }
    // Select mode: a pointerdown on empty canvas begins a marquee (and a no-move
    // release clears the selection). Placement tools begin a drag-to-define.
    setDrag({ start: p, current: p });
    if (activeTool === 'freehand') setFreehandPts([p]);
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    if (!drag) return;
    const p = localPoint(e.clientX, e.clientY);
    if (!p) return;
    setDrag({ start: drag.start, current: p });
    if (activeTool === 'freehand') setFreehandPts((prev) => [...prev, p]);
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>): void {
    if (!drag || !canvasEl) {
      setDrag(null);
      return;
    }
    const end = localPoint(e.clientX, e.clientY) ?? drag.current;
    setDrag(null);
    e.currentTarget.releasePointerCapture(e.pointerId);

    // Normalize drag rect in CSS coords (handle right-to-left / bottom-to-top).
    const cssRect = {
      left: Math.min(drag.start.x, end.x),
      top: Math.min(drag.start.y, end.y),
      width: Math.abs(end.x - drag.start.x),
      height: Math.abs(end.y - drag.start.y),
    };
    const dragged = cssRect.width > DRAG_THRESHOLD_PX || cssRect.height > DRAG_THRESHOLD_PX;

    // CSS → PDF scale (both axes; canvas now has no border so this is exact).
    const cw = canvasEl.clientWidth;
    const ch = canvasEl.clientHeight;
    if (cw === 0 || ch === 0) return;
    const sx = page.width / cw;
    const sy = page.height / ch;
    // Rotation-aware CSS→PDF: a canvas-relative CSS point/rect → PDF coords in
    // the page's ORIGINAL frame (where edits live). At delta 0 these reduce to
    // the plain (x*sx, height − y*sy) maps; for 90/180/270 they undo pdf.js's
    // clockwise canvas rotation. Unit-tested in page-coords.test.ts.
    const canvasBox = { width: cw, height: ch };
    const toPdfPt = (cssX: number, cssY: number): { x: number; y: number } =>
      clickToPdfPointRotated(cssX, cssY, { left: 0, top: 0 }, { ...pageDims, rect: canvasBox }, delta);
    const toPdfRect = (rr: { left: number; top: number; width: number; height: number }) =>
      cssRectToPdfRectRotated(rr, pageDims, canvasBox, delta);

    if (activeTool === 'freehand') {
      // Build the captured stroke into a SEMANTIC /Ink annotation (add-ink).
      // The driver emits a real /Ink with /InkList, so other viewers recognize
      // it as ink — and the append-only annotation keeps the prefix intact.
      const pts = freehandPts.map((p): Pt => toPdfPt(p.x, p.y));
      setFreehandPts([]);
      const simplified = decimate(pts, 1.2 * sx);
      if (simplified.length < 2) return; // a tap, not a stroke
      addEdit({
        kind: 'add-ink',
        page: page.index,
        strokes: freehandInkStrokes(simplified),
        stroke: shapeStyle.stroke ?? DEFAULT_SHAPE_STROKE,
        strokeWidth: Math.max(1, shapeStyle.strokeWidth),
        ...(shapeStyle.opacity < 1 ? { opacity: shapeStyle.opacity } : {}),
      });
      return;
    }

    if (activeTool === 'select') {
      // Marquee: a bare click clears the selection; a drag selects every edit on
      // THIS page whose PDF bbox intersects the marquee rect.
      if (!dragged) {
        selectEdit(null);
        return;
      }
      const mr = toPdfRect(cssRect);
      const m = { x1: mr.x, y1: mr.y, x2: mr.x + mr.width, y2: mr.y + mr.height };
      const hits: number[] = [];
      edits.forEach((ed, i) => {
        if (!('page' in ed) || ed.page !== page.index) return;
        const b = editPdfBounds(ed);
        if (!b) return;
        // axis-aligned bbox overlap test
        if (b.x <= m.x2 && b.x + b.width >= m.x1 && b.y <= m.y2 && b.y + b.height >= m.y1) hits.push(i);
      });
      setSelection(hits);
      return;
    }

    if (activeTool === 'note') {
      // Notes are point anchors. Open an inline editor to type the comment
      // (the annotation /Contents) instead of a fixed placeholder.
      const notePt = toPdfPt(drag.start.x, drag.start.y);
      const pdfX = notePt.x;
      const pdfY = notePt.y;
      setInlineDraft({
        mode: 'note',
        css: { left: drag.start.x, top: drag.start.y, w: 200, fontPx: 14 },
        note: {
          x: Math.max(0, pdfX - NOTE_SIZE_PT / 2),
          y: Math.max(0, pdfY - NOTE_SIZE_PT / 2),
          width: NOTE_SIZE_PT,
          height: NOTE_SIZE_PT,
          color: annoColor,
          ...(annoOpacity < 1 ? { opacity: annoOpacity } : {}),
        },
      });
      return;
    }

    if (activeTool === 'highlight') {
      if (dragged) {
        // Drawn rect → exact position + size in PDF coords (rotation-aware).
        const r = toPdfRect(cssRect);
        addEdit({
          kind: 'add-highlight',
          page: page.index,
          x: r.x,
          y: r.y,
          width: r.width,
          height: r.height,
          color: annoColor,
          ...(annoOpacity < 1 ? { opacity: annoOpacity } : {}),
        });
      } else {
        // Bare click → default size centered on click point (legacy behavior).
        const hp = toPdfPt(drag.start.x, drag.start.y);
        const pdfX = hp.x;
        const pdfY = hp.y;
        addEdit({
          kind: 'add-highlight',
          page: page.index,
          x: Math.max(0, pdfX - HIGHLIGHT_W / 2),
          y: Math.max(0, pdfY - HIGHLIGHT_H / 2),
          width: HIGHLIGHT_W,
          height: HIGHLIGHT_H,
          color: annoColor,
          ...(annoOpacity < 1 ? { opacity: annoOpacity } : {}),
        });
      }
      return;
    }

    if (activeTool === 'shape' && activeShapeType) {
      // Drag-to-define the shape's bounding box; a bare click drops a default
      // size centered on the click. Geometry is generated in PDF space (Y up).
      let bx: number, by: number, bw: number, bh: number;
      if (dragged) {
        const r = toPdfRect(cssRect);
        bx = r.x;
        by = r.y;
        bw = r.width;
        bh = r.height;
      } else {
        bw = SHAPE_DEFAULT_W;
        bh = SHAPE_DEFAULT_H;
        const sp = toPdfPt(drag.start.x, drag.start.y);
        bx = Math.max(0, sp.x - bw / 2);
        by = Math.max(0, sp.y - bh / 2);
      }
      const path = shapePathInPdf(activeShapeType, { x: bx, y: by, width: bw, height: bh });
      if (path) {
        // Use the current shape style (chosen in the style bar) for new shapes;
        // omit a paint when it's set to "none" so the driver draws no fill/stroke.
        addEdit({
          kind: 'add-shape',
          page: page.index,
          x: bx,
          y: by,
          width: bw,
          height: bh,
          shapeType: activeShapeType,
          path,
          ...(shapeStyle.fill ? { fill: shapeStyle.fill } : {}),
          ...(shapeStyle.stroke ? { stroke: shapeStyle.stroke } : {}),
          strokeWidth: shapeStyle.strokeWidth,
          ...(shapeStyle.opacity < 1 ? { opacity: shapeStyle.opacity } : {}),
        });
      }
      return;
    }

    if (activeTool === 'image' && pendingImage) {
      // Place the armed image. A drag fits it (aspect-preserved) inside the box;
      // a bare click drops a default-width copy centered on the click. Then disarm
      // (one image per pick). Move / resize use the existing placed-image handles.
      const aspect =
        pendingImage.naturalWidth > 0 && pendingImage.naturalHeight > 0
          ? pendingImage.naturalWidth / pendingImage.naturalHeight
          : 1;
      let bx: number, by: number, bw: number, bh: number;
      if (dragged) {
        const r = toPdfRect(cssRect);
        if (r.width / r.height > aspect) {
          bh = r.height;
          bw = r.height * aspect;
        } else {
          bw = r.width;
          bh = r.width / aspect;
        }
        bx = r.x;
        by = r.y + r.height - bh; // top-anchored: hold the PDF top edge
      } else {
        bw = page.width * 0.3;
        bh = bw / aspect;
        const ip = toPdfPt(drag.start.x, drag.start.y);
        bx = Math.max(0, ip.x - bw / 2);
        by = Math.max(0, ip.y - bh / 2);
      }
      addEdit({
        kind: 'add-image',
        page: page.index,
        x: bx,
        y: by,
        width: bw,
        height: bh,
        imageBytes: pendingImage.bytes,
      });
      setPendingImage(null);
      return;
    }

    if (activeTool === 'markup' && activeMarkup && dragged) {
      // Sweep over text → mark the runs the rect covers (run-snapped). The rect
      // maps back to the page's original frame, so run-matching works at any
      // page rotation.
      const sel = toPdfRect(cssRect);
      void applyTextMarkup(sel, activeMarkup);
      return;
    }

    if (activeTool === 'line' && activeLineTool) {
      // A line needs a real drag (two distinct points); a bare click is ignored.
      if (!dragged) return;
      const startPt = toPdfPt(drag.start.x, drag.start.y);
      const endPt = toPdfPt(end.x, end.y);
      const x1 = startPt.x;
      const y1 = startPt.y;
      let x2 = endPt.x;
      let y2 = endPt.y;
      if (e.shiftKey) {
        // Snap the line to 0/45/90° relative to the start point.
        const adx = x2 - x1;
        const ady = y2 - y1;
        const ang = Math.round(Math.atan2(ady, adx) / (Math.PI / 4)) * (Math.PI / 4);
        const r = Math.hypot(adx, ady);
        x2 = x1 + Math.cos(ang) * r;
        y2 = y1 + Math.sin(ang) * r;
      }
      // Arrowheads from the tool id: any "Arrow" → end arrow; "Double" → both.
      const dbl = /Double/.test(activeLineTool);
      const endArrow = /Arrow/.test(activeLineTool);
      const startArrow = dbl;
      const stroke = shapeStyle.stroke ?? DEFAULT_SHAPE_STROKE;
      addEdit({
        kind: 'add-line',
        page: page.index,
        x1,
        y1,
        x2,
        y2,
        stroke,
        strokeWidth: Math.max(1, shapeStyle.strokeWidth),
        ...(endArrow ? { endArrow: true } : {}),
        ...(startArrow ? { startArrow: true } : {}),
        ...(shapeStyle.opacity < 1 ? { opacity: shapeStyle.opacity } : {}),
      });
      return;
    }

    if (activeTool === 'text') {
      // Use an existing page font when present; otherwise carry a no-match ref so
      // the driver embeds a subset of the bundled CJK fallback font for this text.
      const fontRef = page.fonts[0]?.ref ?? EMBED_FALLBACK_FONT_REF;
      // Open an inline editor (no window.prompt). Compute the PDF placement and
      // the matching on-screen box; the actual text is typed into the input and
      // committed on Enter/blur.
      // The committed PDF coords are rotation-aware; the inline <input> is
      // positioned in SCREEN space (where the user clicked/drew), so it needs no
      // rotation mapping. `size` is the box height in the page's ORIGINAL frame.
      let x: number, bottomY: number, size: number;
      let cssLeft: number, cssTop: number, cssW: number, cssFontPx: number;
      if (dragged) {
        const r = toPdfRect(cssRect);
        x = r.x;
        bottomY = r.y;
        size = Math.max(8, r.height);
        cssLeft = cssRect.left;
        cssTop = cssRect.top;
        cssW = Math.max(40, cssRect.width);
        cssFontPx = Math.max(8, cssRect.height);
      } else {
        const tp = toPdfPt(drag.start.x, drag.start.y);
        x = tp.x;
        size = TEXT_SIZE_PT;
        bottomY = tp.y - size; // baseline below the click
        cssLeft = drag.start.x;
        cssTop = drag.start.y;
        cssW = 200;
        cssFontPx = size / sy;
      }
      const y = Math.max(0, bottomY);
      setInlineDraft({
        mode: 'text',
        text: { x, y, size, fontRef },
        css: { left: cssLeft, top: cssTop, w: cssW, fontPx: cssFontPx },
      });
    }
  }

  /** Commit (or cancel, when blank) the inline draft (text or note). */
  function commitInlineDraft(value: string): void {
    const d = inlineDraft;
    setInlineDraft(null);
    if (!d) return;
    const text = value.trim();
    if (!text) return;
    if (d.mode === 'text') {
      addEdit({ kind: 'add-text', page: page.index, x: d.text.x, y: d.text.y, text, fontRef: d.text.fontRef, size: d.text.size });
    } else {
      addEdit({
        kind: 'add-annotation',
        page: page.index,
        x: d.note.x,
        y: d.note.y,
        width: d.note.width,
        height: d.note.height,
        note: text,
        color: d.note.color,
        ...(d.note.opacity !== undefined ? { opacity: d.note.opacity } : {}),
      });
    }
  }

  function onPointerCancel(): void {
    setDrag(null);
  }

  // Is this page already marked for deletion? remove-page edits use the
  // *original* page index, which matches `page.index` here.
  const pendingRemoval = edits.some((e) => e.kind === 'remove-page' && e.page === page.index);
  // Pending insert-page edits anchored after THIS page (renders a phantom card).
  const pendingInsertAfter = edits.filter((e) => e.kind === 'insert-page' && e.afterPage === page.index).length;
  const rotateClass =
    placementReady ? 'page--placing' : '';
  const removalClass = pendingRemoval ? 'page--pending-remove' : '';

  async function onInsertAfter(): Promise<void> {
    const { blankPageBytes } = await import('../lib/blank-page.js');
    const bytes = await blankPageBytes(page.width, page.height);
    addEdit({ kind: 'insert-page', afterPage: page.index, pageBytes: bytes });
  }

  // reorder is POSITIONAL: move this page from its current display position to
  // the neighbour. effectivePos is the position in the live (reorder-aware)
  // order, which is exactly what the driver's movePage(from,to) consumes.
  function onMoveUp(): void {
    if (effectivePos <= 0) return;
    addEdit({ kind: 'reorder-page', from: effectivePos, to: effectivePos - 1 });
  }
  function onMoveDown(): void {
    if (effectivePos >= pageCount - 1) return;
    addEdit({ kind: 'reorder-page', from: effectivePos, to: effectivePos + 1 });
  }

  // Image-drop on the canvas → add-image at the drop point.
  async function onDropOnCanvas(e: React.DragEvent<HTMLDivElement>): Promise<void> {
    const files = e.dataTransfer?.files;
    const img = files && [...files].find((f) => /^image\/(png|jpeg)$/i.test(f.type));
    if (!img || !canvasEl) return;
    e.preventDefault();
    e.stopPropagation(); // don't let the editor-level PDF drop handler intercept
    const rect = canvasEl.getBoundingClientRect();
    // Rotation-aware drop point (works at any page rotation).
    const drop = clickToPdfPointRotated(
      e.clientX,
      e.clientY,
      { left: rect.left, top: rect.top },
      { ...pageDims, rect: { width: canvasEl.clientWidth, height: canvasEl.clientHeight } },
      delta,
    );
    const pdfX = drop.x;
    const pdfY = drop.y;
    // Default image box: 25% of the page width, preserving square aspect for now.
    const W = page.width * 0.25;
    const H = page.height * 0.25;
    const bytes = new Uint8Array(await img.arrayBuffer());
    addEdit({
      kind: 'add-image',
      page: page.index,
      x: Math.max(0, pdfX - W / 2),
      y: Math.max(0, pdfY - H / 2),
      width: W,
      height: H,
      imageBytes: bytes,
    });
  }
  function onDragOverCanvas(e: React.DragEvent<HTMLDivElement>): void {
    if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
  }

  return (
    <>
      <article
        className={`page ${rotateClass} ${removalClass}`.trim().replace(/\s+/g, ' ')}
        data-page-index={page.index}
      >
        <div className="page__bar">
          <span className="page-card__no">
            P{effectivePos + 1}
            {effectivePos !== page.index && <span className="page__moved"> ←元P{page.index + 1}</span>}
          </span>
          <span className="page-card__meta">
            {Math.round(page.width)}×{Math.round(page.height)}pt · {rotation}°
            {rotation !== page.rotation && <span className="page__edited"> 編集</span>}
            {pendingRemoval && <span className="page__remove-tag"> 削除予定</span>}
          </span>
          <span className="page__bar-actions">
            <button
              className="btn btn--icon"
              disabled={pendingRemoval || effectivePos === 0}
              onClick={onMoveUp}
              title="このページを上に移動"
              aria-label="ページを上に移動"
            >
              ↑
            </button>
            <button
              className="btn btn--icon"
              disabled={pendingRemoval || effectivePos >= pageCount - 1}
              onClick={onMoveDown}
              title="このページを下に移動"
              aria-label="ページを下に移動"
            >
              ↓
            </button>
            <button
              className="btn"
              disabled={pendingRemoval}
              onClick={() => addEdit({ kind: 'rotate-page', page: page.index, to: rotateCW(rotation) })}
            >
              90° 回転
            </button>
            <button
              className="btn"
              disabled={pendingRemoval}
              onClick={onInsertAfter}
              title="このページの直後に空白ページを挿入"
            >
              + ページ
            </button>
            <button
              className="btn btn--danger"
              disabled={pendingRemoval}
              onClick={() => addEdit({ kind: 'remove-page', page: page.index })}
              title="保存時にこのページを削除する"
            >
              削除
            </button>
          </span>
        </div>
        <div
          className="page__canvas-wrap"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onDragOver={onDragOverCanvas}
          onDrop={onDropOnCanvas}
        >
          <PageCanvas
            doc={pdfDoc}
            pageNumber={page.index + 1}
            rotation={rotation}
            scale={scale}
            onCanvas={setCanvasEl}
          />
          <PageOverlay
            edits={edits}
            pageIndex={page.index}
            page={page}
            rotation={rotation}
            canvasEl={canvasEl}
            interactive={activeTool === 'select'}
            removeEdit={removeEdit}
          />
          {drag &&
            (() => {
              const box = {
                position: 'absolute' as const,
                left: `${Math.min(drag.start.x, drag.current.x)}px`,
                top: `${Math.min(drag.start.y, drag.current.y)}px`,
                width: `${Math.abs(drag.current.x - drag.start.x)}px`,
                height: `${Math.abs(drag.current.y - drag.start.y)}px`,
              };
              const dragged2 =
                Math.abs(drag.current.x - drag.start.x) > 2 || Math.abs(drag.current.y - drag.start.y) > 2;
              // Freehand: preview the live ink stroke as it's drawn.
              if (activeTool === 'freehand' && freehandPts.length > 1) {
                return (
                  <svg
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}
                    aria-hidden="true"
                  >
                    <polyline
                      points={freehandPts.map((p) => `${p.x},${p.y}`).join(' ')}
                      fill="none"
                      stroke={rgbCss(shapeStyle.stroke ?? DEFAULT_SHAPE_STROKE, shapeStyle.opacity)}
                      strokeWidth={Math.max(1, shapeStyle.strokeWidth)}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                );
              }
              // Select tool: a marquee rectangle (selection lasso).
              if (activeTool === 'select') {
                return <div className="ovl ovl--marquee" style={box} />;
              }
              // Line tool: preview the actual line + arrowheads as you drag.
              if (activeTool === 'line' && activeLineTool && dragged2) {
                const a = drag.start;
                const b = drag.current;
                const col = rgbCss(shapeStyle.stroke ?? DEFAULT_SHAPE_STROKE, shapeStyle.opacity);
                const w = Math.max(1, shapeStyle.strokeWidth);
                const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
                const poly = (tip: { x: number; y: number }, dx: number, dy: number): string => {
                  const d = Math.hypot(dx, dy) || 1;
                  const ux = dx / d;
                  const uy = dy / d;
                  const aLen = Math.max(7, Math.min(len * 0.5, 14));
                  const aHalf = aLen * 0.45;
                  const bx = tip.x - ux * aLen;
                  const by = tip.y - uy * aLen;
                  return `${tip.x},${tip.y} ${bx - uy * aHalf},${by + ux * aHalf} ${bx + uy * aHalf},${by - ux * aHalf}`;
                };
                return (
                  <svg
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}
                    aria-hidden="true"
                  >
                    <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={col} strokeWidth={w} strokeLinecap="round" />
                    {/Arrow/.test(activeLineTool) && <polygon points={poly(b, b.x - a.x, b.y - a.y)} fill={col} />}
                    {/Double/.test(activeLineTool) && <polygon points={poly(a, a.x - b.x, a.y - b.y)} fill={col} />}
                  </svg>
                );
              }
              // While dragging out a shape, preview the SHAPE itself (live
              // silhouette stretched to the drag box) rather than a bare rect —
              // matches PowerPoint's drag-to-draw. Other tools keep the rect.
              const shapeD =
                activeTool === 'shape' && activeShapeType ? shapeSvgPathD(activeShapeType, 100) : null;
              if (shapeD && (Math.abs(drag.current.x - drag.start.x) > 2 || Math.abs(drag.current.y - drag.start.y) > 2)) {
                return (
                  <div className="ovl ovl--shape ovl--shape-preview" style={box}>
                    <svg
                      className="ovl__shape-svg"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      <path
                        d={shapeD}
                        fill={shapeStyle.fill ? rgbCss(shapeStyle.fill, 0.5 * shapeStyle.opacity) : 'none'}
                        stroke={shapeStyle.stroke ? rgbCss(shapeStyle.stroke, shapeStyle.opacity) : 'none'}
                        strokeWidth={Math.max(1, shapeStyle.strokeWidth)}
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  </div>
                );
              }
              // Image tool: preview the actual image (aspect-fit, dimmed) in the box.
              if (activeTool === 'image' && pendingImageUrl && dragged2) {
                return (
                  <div className="ovl ovl--drag" style={box}>
                    <img
                      src={pendingImageUrl}
                      alt=""
                      aria-hidden="true"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.6, pointerEvents: 'none' }}
                    />
                  </div>
                );
              }
              return <div className="ovl ovl--drag" style={box} />;
            })()}
          {inlineDraft && (
            <input
              className="text-draft"
              autoFocus
              defaultValue=""
              placeholder={inlineDraft.mode === 'note' ? 'コメントを入力 (Enter)' : 'テキストを入力 (Enter)'}
              style={{
                left: `${inlineDraft.css.left}px`,
                top: `${inlineDraft.css.top}px`,
                width: `${inlineDraft.css.w}px`,
                fontSize: `${Math.max(8, inlineDraft.css.fontPx)}px`,
                lineHeight: `${Math.max(8, inlineDraft.css.fontPx)}px`,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  commitInlineDraft((e.target as HTMLInputElement).value);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  setInlineDraft(null);
                }
              }}
              onBlur={(e) => commitInlineDraft(e.target.value)}
            />
          )}
        </div>
        {/* Hint when a tool is armed but placement is blocked by rotation. */}
        {activeTool !== 'select' && rotation !== page.rotation && (
          <p className="page__hint">回転中は配置できません — 元の向きに戻してください</p>
        )}
      </article>
      {/* Phantom "insert here" cards rendered AFTER this page, one per pending insert-page edit. */}
      {Array.from({ length: pendingInsertAfter }, (_, i) => (
        <article key={`ins-${page.index}-${i}`} className="page page--inserted">
          <p className="page__inserted-label">＋ ページ挿入予定 (P{page.index + 1} の直後)</p>
        </article>
      ))}
    </>
  );
}
